// GDPR-compliant Google Analytics utility
// Only loads and tracks when user has consented to analytics cookies

import { getCookiePreferences } from './cookieManager';

const GA_MEASUREMENT_ID = 'G-GBHGGRPFVR';
const GA_ADS_ID = 'AW-17175439992';

let isAnalyticsLoaded = false;
let isAdsLoaded = false;

// Load Google Analytics script dynamically
export const loadGoogleAnalytics = () => {
  return new Promise((resolve, reject) => {
    if (isAnalyticsLoaded) {
      resolve();
      return;
    }

    try {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];
      
      // Define gtag function
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      // Load the Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      script.onload = () => {
        // Configure Google Analytics
        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID, {
          // Privacy-friendly settings
          anonymize_ip: true,
          send_page_view: false, // We'll send page views manually
          cookie_flags: 'SameSite=Strict;Secure'
        });
        
        isAnalyticsLoaded = true;
        console.log('Google Analytics loaded with user consent');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics'));
      };
      
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

// Load Google Ads script dynamically
export const loadGoogleAds = () => {
  return new Promise((resolve, reject) => {
    if (isAdsLoaded) {
      resolve();
      return;
    }

    try {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];
      
      // Define gtag function if not already defined
      if (!window.gtag) {
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
      }

      // Load the Google Ads script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ADS_ID}`;
      
      script.onload = () => {
        // Configure Google Ads
        window.gtag('js', new Date());
        window.gtag('config', GA_ADS_ID);
        
        isAdsLoaded = true;
        console.log('Google Ads loaded with user consent');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Ads'));
      };
      
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

// Initialize analytics based on user preferences
export const initializeAnalytics = async () => {
  const preferences = getCookiePreferences();
  
  try {
    if (preferences.analytics) {
      await loadGoogleAnalytics();
    }
    
    if (preferences.marketing) {
      await loadGoogleAds();
    }
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
};

// Safe analytics tracking function
export const trackEvent = (eventName, parameters = {}) => {
  const preferences = getCookiePreferences();
  
  // Only track if analytics are enabled and gtag is available
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('event', eventName, parameters);
  }
};

// Safe page view tracking
export const trackPageView = (page_title, page_location) => {
  const preferences = getCookiePreferences();
  
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('event', 'page_view', {
      page_title,
      page_location,
      page_path: window.location.pathname
    });
  }
};

// Safe conversion tracking
export const trackConversion = (conversionId, parameters = {}) => {
  const preferences = getCookiePreferences();
  
  if (preferences.marketing && window.gtag && isAdsLoaded) {
    window.gtag('event', 'conversion', {
      send_to: conversionId,
      ...parameters
    });
  }
};

// Safe user identification (for logged-in users)
export const setUserId = (userId) => {
  const preferences = getCookiePreferences();
  
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }
};

// Remove analytics cookies and scripts when consent is withdrawn
export const removeAnalytics = () => {
  // Remove Google Analytics cookies
  const gaCookies = ['_ga', '_ga_' + GA_MEASUREMENT_ID.replace('G-', ''), '_gid', '_gat'];
  gaCookies.forEach(cookie => {
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  // Remove Google Ads cookies
  const adsCookies = ['_gcl_au', '_gcl_dc', '_gcl_aw'];
  adsCookies.forEach(cookie => {
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  // Reset loaded states
  isAnalyticsLoaded = false;
  isAdsLoaded = false;
  
  // Clear gtag if it exists
  if (window.gtag) {
    delete window.gtag;
  }
  
  console.log('Analytics tracking disabled and cookies removed');
};

// Utility to check if analytics is enabled
export const isAnalyticsEnabled = () => {
  const preferences = getCookiePreferences();
  return preferences.analytics;
};

// Utility to check if marketing is enabled
export const isMarketingEnabled = () => {
  const preferences = getCookiePreferences();
  return preferences.marketing;
};
